import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatters';
import { 
  Users, 
  Wallet, 
  TrendingUp, 
  Clock,
  ArrowDownRight,
  ArrowUpRight,
  ChevronRight,
  Bell,
  AlertCircle,
  Activity,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [recentDeposits, setRecentDeposits] = useState([]);
  const [recentWithdrawals, setRecentWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearingCache, setClearingCache] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, depositsRes, withdrawalsRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/deposits`),
        axios.get(`${API_URL}/admin/withdrawals`),
        axios.get(`${API_URL}/admin/alerts`)
      ]);
      setStats(statsRes.data);
      setRecentDeposits(depositsRes.data.slice(0, 5));
      setRecentWithdrawals(withdrawalsRes.data.slice(0, 5));
      setAlerts(alertsRes.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      // Clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Clear localStorage cache items (keep auth)
      const authToken = localStorage.getItem('token');
      const authUser = localStorage.getItem('user');
      localStorage.clear();
      if (authToken) localStorage.setItem('token', authToken);
      if (authUser) localStorage.setItem('user', authUser);
      
      // Increment version to force reload for all users
      await axios.post(`${API_URL}/admin/clear-cache`);
      
      toast.success('Cache limpa com sucesso! Os utilizadores verão a versão atualizada.');
      
      // Force reload the page
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Erro ao limpar cache');
    } finally {
      setClearingCache(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            {t('admin.dashboard')}
          </h1>
          <p className="text-slate-400 mt-1">{t('admin.recentActivity')}</p>
        </div>
        <Button
          onClick={handleClearCache}
          disabled={clearingCache}
          variant="outline"
          className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300"
          data-testid="clear-cache-btn"
        >
          {clearingCache ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              A limpar...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Limpar Cache
            </>
          )}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-card p-6" data-testid="stat-clients">
          <div className="flex items-center gap-3 text-slate-400 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm font-medium">{t('admin.totalClients')}</span>
          </div>
          <div className="text-3xl font-bold text-white font-['Outfit']">
            {stats?.total_clients || 0}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="stat-capital">
          <div className="flex items-center gap-3 text-slate-400 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-sm font-medium">{t('admin.totalCapital')}</span>
          </div>
          <div className="text-3xl font-bold text-white font-['Outfit']">
            {formatCurrency(stats?.total_capital)}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="stat-returns">
          <div className="flex items-center gap-3 text-slate-400 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-sm font-medium">{t('admin.totalReturns')}</span>
          </div>
          <div className="text-3xl font-bold text-white font-['Outfit']">
            {formatCurrency(stats?.total_returns)}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="stat-pending">
          <div className="flex items-center gap-3 text-slate-400 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-sm font-medium">{t('common.pending')}</span>
          </div>
          <div className="text-3xl font-bold text-white font-['Outfit']">
            {(stats?.pending_deposits || 0) + (stats?.pending_withdrawals || 0)}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {stats?.pending_deposits} {t('nav.deposits').toLowerCase()}, {stats?.pending_withdrawals} {t('nav.withdrawals').toLowerCase()}
          </p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Deposits */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white font-['Outfit'] flex items-center gap-2">
              <ArrowDownRight className="w-5 h-5 text-emerald-400" />
              {t('admin.pendingDeposits')}
            </h2>
            <Link 
              to="/admin/deposits" 
              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
            >
              {t('common.viewAll')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentDeposits.length > 0 ? (
            <div className="space-y-3">
              {recentDeposits.slice(0, 5).map((deposit) => (
                <div 
                  key={deposit.id}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-xl"
                >
                  <div>
                    <p className="text-white font-medium">{formatCurrency(deposit.amount)}</p>
                    <p className="text-sm text-slate-400">
                      {new Date(deposit.created_at).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    deposit.status === 'pending'
                      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      : deposit.status === 'approved'
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}>
                    {deposit.status === 'pending' ? t('common.pending') : deposit.status === 'approved' ? t('common.approved') : t('common.rejected')}
                  </span>
                </div>
              ))}
              {recentDeposits.length > 5 && (
                <Link 
                  to="/admin/deposits" 
                  className="block text-center py-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Ver mais {recentDeposits.length - 5} depósitos →
                </Link>
              )}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">{t('common.noData')}</p>
          )}
        </div>

        {/* Pending Withdrawals */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white font-['Outfit'] flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-red-400" />
              {t('admin.pendingWithdrawals')}
            </h2>
            <Link 
              to="/admin/withdrawals" 
              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
            >
              {t('common.viewAll')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentWithdrawals.length > 0 ? (
            <div className="space-y-3">
              {recentWithdrawals.map((withdrawal) => (
                <div 
                  key={withdrawal.id}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-xl"
                >
                  <div>
                    <p className="text-white font-medium">{formatCurrency(withdrawal.amount)}</p>
                    <p className="text-sm text-slate-400">
                      {new Date(withdrawal.created_at).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    withdrawal.status === 'pending' 
                      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      : withdrawal.status === 'approved'
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                  }`}>
                    {withdrawal.status === 'pending' ? t('common.pending') : withdrawal.status === 'approved' ? t('common.approved') : t('common.rejected')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">{t('common.noData')}</p>
          )}
        </div>
      </div>

      {/* Alerts Panel */}
      {alerts && (alerts.pending_deposits_count > 0 || alerts.pending_withdrawals_count > 0) && (
        <div className="glass-card p-6 border border-amber-500/20 bg-amber-500/5" data-testid="alerts-panel">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{t('admin.alerts')}</h2>
              <p className="text-sm text-slate-400">{t('admin.actionsRequired')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pending Deposits Alert */}
            {alerts.pending_deposits_count > 0 && (
              <Link 
                to="/admin/deposits"
                className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/20 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <ArrowDownRight className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-emerald-400 font-semibold">{alerts.pending_deposits_count}</p>
                  <p className="text-sm text-slate-300">{t('admin.pendingDeposits')}</p>
                </div>
              </Link>
            )}

            {/* Pending Withdrawals Alert */}
            {alerts.pending_withdrawals_count > 0 && (
              <Link 
                to="/admin/withdrawals"
                className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl hover:bg-red-500/20 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-red-400 font-semibold">{alerts.pending_withdrawals_count}</p>
                  <p className="text-sm text-slate-300">{t('admin.pendingWithdrawals')}</p>
                </div>
              </Link>
            )}
          </div>

          {/* Activity Summary */}
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-4 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              <span>{t('admin.last24Hours')}:</span>
            </div>
            <span className="text-emerald-400">{alerts.recent_deposits_24h} {t('nav.deposits').toLowerCase()}</span>
            <span className="text-red-400">{alerts.recent_withdrawals_24h} {t('nav.withdrawals').toLowerCase()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
